import {
    BigCommercePaymentsFastlaneUtils,
    BigCommercePaymentsInitializationData,
    getFastlaneStyles,
    isBigcommerceFastlaneRequestError,
    isPayPalFastlaneCustomer,
    PayPalFastlaneAuthenticationState,
    PayPalFastlaneCardComponentMethods,
    PayPalFastlaneCardComponentOptions,
    PayPalFastlanePaymentFormattedPayload,
    PayPalFastlaneSdk,
    PayPalSdkHelper,
    TDSecureAuthenticationState,
    TDSecureVerificationMethod,
} from '@bigcommerce/checkout-sdk/bigcommerce-payments-utils';
import {
    CardInstrument,
    InvalidArgumentError,
    isHostedInstrumentLike,
    isVaultedInstrument,
    OrderFinalizationNotRequiredError,
    OrderPaymentRequestBody,
    OrderRequestBody,
    Payment,
    PaymentArgumentInvalidError,
    PaymentInitializeOptions,
    PaymentIntegrationService,
    PaymentMethodClientUnavailableError,
    PaymentMethodInvalidError,
    PaymentRequestOptions,
    PaymentStrategy,
    VaultedInstrument,
} from '@bigcommerce/checkout-sdk/payment-integration-api';
import { isExperimentEnabled } from '@bigcommerce/checkout-sdk/utility';

import BigCommercePaymentsRequestSender from '../bigcommerce-payments-request-sender';
import { LiabilityShiftEnum } from '../bigcommerce-payments-types';

import BigCommercePaymentsFastlanePaymentInitializeOptions, {
    WithBigCommercePaymentsFastlanePaymentInitializeOptions,
} from './bigcommerce-payments-fastlane-payment-initialize-options';

export default class BigCommercePaymentsFastlanePaymentStrategy implements PaymentStrategy {
    private paypalComponentMethods?: PayPalFastlaneCardComponentMethods;
    private threeDSVerificationMethod?: string;
    private paypalFastlaneSdk?: PayPalFastlaneSdk;
    private bigcommerce_payments_fastlane?: BigCommercePaymentsFastlanePaymentInitializeOptions;
    private methodId?: string;
    private orderId?: string;

    constructor(
        private paymentIntegrationService: PaymentIntegrationService,
        private bigCommercePaymentsRequestSender: BigCommercePaymentsRequestSender,
        private bigCommercePaymentsSdk: PayPalSdkHelper,
        private bigCommercePaymentsFastlaneUtils: BigCommercePaymentsFastlaneUtils,
    ) {}

    /**
     *
     * Default methods
     *
     * */
    async initialize(
        options: PaymentInitializeOptions & WithBigCommercePaymentsFastlanePaymentInitializeOptions,
    ): Promise<void> {
        const { methodId, bigcommerce_payments_fastlane } = options;

        if (!methodId) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.methodId" argument is not provided.',
            );
        }

        this.methodId = methodId;

        if (!bigcommerce_payments_fastlane) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.bigcommerce_payments_fastlane" argument is not provided.',
            );
        }

        this.bigcommerce_payments_fastlane = bigcommerce_payments_fastlane;

        if (
            !bigcommerce_payments_fastlane.onInit ||
            typeof bigcommerce_payments_fastlane.onInit !== 'function'
        ) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.bigcommerce_payments_fastlane.onInit" argument is not provided or it is not a function.',
            );
        }

        if (
            !bigcommerce_payments_fastlane.onChange ||
            typeof bigcommerce_payments_fastlane.onChange !== 'function'
        ) {
            throw new InvalidArgumentError(
                'Unable to initialize payment because "options.bigcommerce_payments_fastlane.onChange" argument is not provided or it is not a function.',
            );
        }

        await this.paymentIntegrationService.loadPaymentMethod(methodId);

        const state = this.paymentIntegrationService.getState();
        const cart = state.getCartOrThrow();
        const paymentMethod =
            state.getPaymentMethodOrThrow<BigCommercePaymentsInitializationData>(methodId);
        const { isDeveloperModeApplicable, isFastlaneStylingEnabled } =
            paymentMethod.initializationData || {};

        this.threeDSVerificationMethod =
            paymentMethod.initializationData?.threeDSVerificationMethod;

        this.paypalFastlaneSdk = await this.bigCommercePaymentsSdk.getPayPalFastlaneSdk(
            paymentMethod,
            cart.currency.code,
            cart.id,
        );

        const paypalFastlaneStyling = isFastlaneStylingEnabled
            ? paymentMethod?.initializationData?.fastlaneStyles
            : {};

        const fastlaneStyles = getFastlaneStyles(
            paypalFastlaneStyling,
            bigcommerce_payments_fastlane?.styles,
        );

        await this.bigCommercePaymentsFastlaneUtils.initializePayPalFastlane(
            this.paypalFastlaneSdk,
            !!isDeveloperModeApplicable,
            fastlaneStyles,
        );

        if (this.shouldRunAuthenticationFlow()) {
            await this.runPayPalAuthenticationFlowOrThrow(methodId);
        }

        await this.initializePayPalPaymentComponent();

        bigcommerce_payments_fastlane.onInit((container: string) =>
            this.renderPayPalPaymentComponent(container),
        );
        bigcommerce_payments_fastlane.onChange(() =>
            this.handlePayPalStoredInstrumentChange(methodId),
        );
    }

    async execute(orderRequest: OrderRequestBody, options?: PaymentRequestOptions): Promise<void> {
        const { payment, ...order } = orderRequest;

        if (!payment) {
            throw new PaymentArgumentInvalidError(['payment']);
        }

        const { paymentData, methodId } = payment;

        const isVaultedFlow = paymentData && isVaultedInstrument(paymentData);

        try {
            await this.paymentIntegrationService.submitOrder(order, options);

            const paymentPayload = isVaultedFlow
                ? await this.prepareVaultedInstrumentPaymentPayload(methodId, paymentData)
                : await this.preparePaymentPayload(methodId, paymentData);

            await this.paymentIntegrationService.submitPayment<PayPalFastlanePaymentFormattedPayload>(
                paymentPayload,
            );

            this.bigCommercePaymentsFastlaneUtils.removeStorageSessionId();
        } catch (error) {
            if (
                isBigcommerceFastlaneRequestError(error) &&
                error.response.name === 'INVALID_REQUEST'
            ) {
                const invalidRequestError = {
                    translationKey: 'payment.errors.invalid_request_error',
                };

                this.handleError(invalidRequestError);

                return Promise.reject();
            }

            if (error instanceof Error && error.name !== 'FastlaneError') {
                throw error;
            }

            return Promise.reject();
        }
    }

    finalize(): Promise<void> {
        return Promise.reject(new OrderFinalizationNotRequiredError());
    }

    async deinitialize(): Promise<void> {
        return Promise.resolve();
    }

    /**
     *
     * Authentication flow methods
     *
     */
    private shouldRunAuthenticationFlow(): boolean {
        const state = this.paymentIntegrationService.getState();
        const cart = state.getCartOrThrow();
        const customer = state.getCustomerOrThrow();
        const paymentProviderCustomer = state.getPaymentProviderCustomer();
        const paypalFastlaneCustomer = isPayPalFastlaneCustomer(paymentProviderCustomer)
            ? paymentProviderCustomer
            : {};

        const paypalFastlaneSessionId = this.bigCommercePaymentsFastlaneUtils.getStorageSessionId();

        if (
            !customer.isGuest ||
            paypalFastlaneCustomer?.authenticationState ===
                PayPalFastlaneAuthenticationState.CANCELED
        ) {
            return false;
        }

        return !paypalFastlaneCustomer?.authenticationState && paypalFastlaneSessionId === cart.id;
    }

    private async runPayPalAuthenticationFlowOrThrow(methodId: string): Promise<void> {
        try {
            const state = this.paymentIntegrationService.getState();
            const cart = state.getCartOrThrow();
            const customer = state.getCustomer();
            const billingAddress = state.getBillingAddress();
            const customerEmail = customer?.email || billingAddress?.email || '';

            const { customerContextId } =
                await this.bigCommercePaymentsFastlaneUtils.lookupCustomerOrThrow(customerEmail);

            const authenticationResult =
                await this.bigCommercePaymentsFastlaneUtils.triggerAuthenticationFlowOrThrow(
                    customerContextId,
                );

            const { authenticationState, addresses, instruments } =
                this.bigCommercePaymentsFastlaneUtils.mapPayPalFastlaneProfileToBcCustomerData(
                    methodId,
                    authenticationResult,
                );

            await this.paymentIntegrationService.updatePaymentProviderCustomer({
                authenticationState,
                addresses,
                instruments,
            });

            const isAuthenticationFlowCanceled =
                authenticationResult.authenticationState ===
                PayPalFastlaneAuthenticationState.CANCELED;

            if (isAuthenticationFlowCanceled) {
                this.bigCommercePaymentsFastlaneUtils.removeStorageSessionId();
            } else {
                this.bigCommercePaymentsFastlaneUtils.updateStorageSessionId(cart.id);
            }
        } catch (error) {
            // Info: Do not throw anything here to avoid blocking customer from passing checkout flow
            this.handleErrorLog(error);
        }
    }

    /**
     *
     * BigCommercePayments Fastlane Card Component rendering method
     *
     */
    private async initializePayPalPaymentComponent(): Promise<void> {
        const state = this.paymentIntegrationService.getState();
        const billingAddress = state.getBillingAddressOrThrow();
        const phone = billingAddress.phone;
        const fullName = `${billingAddress.firstName} ${billingAddress.lastName}`.trim();

        const paypalFastlane = this.bigCommercePaymentsFastlaneUtils.getPayPalFastlaneOrThrow();

        const cardComponentOptions: PayPalFastlaneCardComponentOptions = {
            fields: {
                cardholderName: {
                    prefill: fullName,
                    enabled: true,
                },
                ...(phone && {
                    phoneNumber: {
                        prefill: phone,
                    },
                }),
            },
        };

        this.paypalComponentMethods = await paypalFastlane.FastlaneCardComponent(
            cardComponentOptions,
        );
    }

    private renderPayPalPaymentComponent(container?: string): void {
        const paypalComponentMethods = this.getPayPalComponentMethodsOrThrow();

        if (!container) {
            throw new InvalidArgumentError(
                'Unable to render card component because "container" argument is not provided.',
            );
        }

        paypalComponentMethods.render(container);
    }

    private getPayPalComponentMethodsOrThrow(): PayPalFastlaneCardComponentMethods {
        if (!this.paypalComponentMethods) {
            throw new PaymentMethodClientUnavailableError();
        }

        return this.paypalComponentMethods;
    }

    /**
     *
     * Payment Payload preparation methods
     *
     */
    private async prepareVaultedInstrumentPaymentPayload(
        methodId: string,
        paymentData: VaultedInstrument,
    ): Promise<Payment<PayPalFastlanePaymentFormattedPayload>> {
        const { instrumentId } = paymentData;
        const state = this.paymentIntegrationService.getState();
        const paymentMethod =
            state.getPaymentMethodOrThrow<BigCommercePaymentsInitializationData>(methodId);

        const is3DSEnabled =
            this.isBigcommercePaymentsFastlaneThreeDSAvailable() &&
            paymentMethod.config.is3dsEnabled;

        const nonce = is3DSEnabled ? await this.get3DSNonce(instrumentId) : instrumentId;

        await this.createOrder(nonce);

        return {
            methodId,
            paymentData: {
                formattedPayload: {
                    paypal_fastlane_token: {
                        order_id: this.orderId,
                        token: nonce,
                    },
                },
            },
        };
    }

    private async preparePaymentPayload(
        methodId: string,
        paymentData: OrderPaymentRequestBody['paymentData'],
    ): Promise<Payment<PayPalFastlanePaymentFormattedPayload>> {
        const state = this.paymentIntegrationService.getState();
        const billingAddress = state.getBillingAddressOrThrow();
        const paymentMethod =
            state.getPaymentMethodOrThrow<BigCommercePaymentsInitializationData>(methodId);

        const fullName = `${billingAddress.firstName} ${billingAddress.lastName}`.trim();

        const { getPaymentToken } = this.getPayPalComponentMethodsOrThrow();

        const { id } = await getPaymentToken({
            name: { fullName },
            billingAddress:
                this.bigCommercePaymentsFastlaneUtils.mapBcToPayPalAddress(billingAddress),
        });

        const is3DSEnabled =
            this.isBigcommercePaymentsFastlaneThreeDSAvailable() &&
            paymentMethod.config.is3dsEnabled;

        const { shouldSaveInstrument = false, shouldSetAsDefaultInstrument = false } =
            isHostedInstrumentLike(paymentData) ? paymentData : {};

        const nonce = is3DSEnabled ? await this.get3DSNonce(id) : id;

        await this.createOrder(nonce);

        return {
            methodId,
            paymentData: {
                ...paymentData,
                shouldSaveInstrument,
                shouldSetAsDefaultInstrument,
                formattedPayload: {
                    paypal_fastlane_token: {
                        order_id: this.orderId,
                        token: nonce,
                    },
                },
            },
        };
    }

    private async createOrder(id: string): Promise<void> {
        const state = this.paymentIntegrationService.getState();
        const cartId = state.getCartOrThrow().id;

        if (this.methodId) {
            const { orderId } = await this.bigCommercePaymentsRequestSender.createOrder(
                this.methodId,
                {
                    cartId,
                    fastlaneToken: id,
                },
            );

            this.orderId = orderId;
        }
    }

    /**
     *
     * 3DSecure methods
     *
     * */
    private async get3DSNonce(nonce: string): Promise<string> {
        const state = this.paymentIntegrationService.getState();
        const cart = state.getCartOrThrow();
        const order = state.getOrderOrThrow();
        const checkoutUrl = state.getStoreConfigOrThrow().links.checkoutLink;

        const threeDomainSecureComponent = this.paypalFastlaneSdk?.ThreeDomainSecureClient;

        if (!threeDomainSecureComponent) {
            throw new PaymentMethodClientUnavailableError();
        }

        const threeDomainSecureParameters = {
            amount: order.orderAmount.toFixed(2),
            currency: cart.currency.code,
            nonce,
            threeDSRequested: this.threeDSVerificationMethod === TDSecureVerificationMethod.Always,
            transactionContext: {
                experience_context: {
                    locale: 'en-US',
                    return_url: checkoutUrl,
                    cancel_url: checkoutUrl,
                },
            },
        };

        const isThreeDomainSecureEligible = await threeDomainSecureComponent.isEligible(
            threeDomainSecureParameters,
        );

        if (
            this.threeDSVerificationMethod === TDSecureVerificationMethod.Always &&
            !isThreeDomainSecureEligible
        ) {
            throw new PaymentMethodInvalidError();
        }

        if (isThreeDomainSecureEligible) {
            const {
                liabilityShift,
                authenticationState,
                nonce: threeDSNonce,
            } = await threeDomainSecureComponent.show();

            if (
                liabilityShift === LiabilityShiftEnum.No ||
                liabilityShift === LiabilityShiftEnum.Unknown ||
                authenticationState === TDSecureAuthenticationState.Errored ||
                authenticationState === TDSecureAuthenticationState.Cancelled
            ) {
                throw new PaymentMethodInvalidError();
            }

            if (
                authenticationState === TDSecureAuthenticationState.Succeeded &&
                [LiabilityShiftEnum.Yes, LiabilityShiftEnum.Possible].includes(liabilityShift)
            ) {
                return threeDSNonce;
            }
        }

        return nonce;
    }

    /**
     *
     * BigCommercePayments Fastlane instrument change
     *
     */
    private async handlePayPalStoredInstrumentChange(
        methodId: string,
    ): Promise<CardInstrument | undefined> {
        const paypalAxoSdk = this.bigCommercePaymentsFastlaneUtils.getPayPalFastlaneOrThrow();

        const { selectionChanged, selectedCard } = await paypalAxoSdk.profile.showCardSelector();

        if (selectionChanged) {
            const state = this.paymentIntegrationService.getState();
            const paymentProviderCustomer = state.getPaymentProviderCustomer();
            const paypalFastlaneCustomer = isPayPalFastlaneCustomer(paymentProviderCustomer)
                ? paymentProviderCustomer
                : {};

            const selectedInstrument =
                this.bigCommercePaymentsFastlaneUtils.mapPayPalToBcInstrument(
                    methodId,
                    selectedCard,
                )[0];

            await this.paymentIntegrationService.updatePaymentProviderCustomer({
                ...paypalFastlaneCustomer,
                instruments: [selectedInstrument],
            });

            return selectedInstrument;
        }

        return undefined;
    }

    /**
     *
     * Bigcommerce Payments Fastlane experiments handling
     *
     */
    private isBigcommercePaymentsFastlaneThreeDSAvailable(): boolean {
        const state = this.paymentIntegrationService.getState();
        const features = state.getStoreConfigOrThrow().checkoutSettings.features;

        return isExperimentEnabled(features, 'PROJECT-7080.bcp_fastlane_three_ds');
    }

    private handleError(error: unknown): void {
        if (
            this.bigcommerce_payments_fastlane?.onError &&
            typeof this.bigcommerce_payments_fastlane.onError === 'function'
        ) {
            this.bigcommerce_payments_fastlane.onError(error);
        }
    }

    private handleErrorLog(error: unknown): void {
        if (
            this.bigcommerce_payments_fastlane?.onErrorLog &&
            typeof this.bigcommerce_payments_fastlane.onErrorLog === 'function'
        ) {
            this.bigcommerce_payments_fastlane.onErrorLog(error);
        }
    }
}
